import { Form, Formik } from "formik";
import { observer } from "mobx-react";
import * as React from "react";
import * as Yup from "yup";
import { phoneRegex } from "../../constants";
import { t } from "../../i18n/util";
import { applicationStore } from "../../stores/ApplicationStore";
import { THEME } from "../../theme/ThemeStore";
import { ApplicationValues } from "../../types/models";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { ButtonContainer } from "../ui/ButtonContainer";
import { ErrorMessagesList, HrBotButton } from "../ui/Primitives";
import { PersonalDataGrid } from "./PersonalDataGrid";

type Props = { onSubmit: (application: ApplicationValues) => void };

export const PersonalDataForm = observer(({ onSubmit }: Props) => {
    const isMobile = useMobileMediaQuery();

    return (
        <>
            <Formik
                initialValues={applicationStore.currentApplication}
                onSubmit={onSubmit}
                validateOnChange
                validateOnBlur
                validationSchema={Yup.object().shape({
                    firstName: Yup.string().required(t("error.required")),
                    lastName: Yup.string().required(t("error.required")),
                    jobCategory: Yup.string().nullable().required(t("error.required")),
                    jobCategoryId: Yup.string().nullable().required(t("error.required")),
                    phone: Yup.string().required(t("error.required")).matches(phoneRegex, t("error.phone")),
                    email: Yup.string().required(t("error.required")).email(t("error.email")).trim(),
                    previousProjects: Yup.string().url(t("error.website")),
                    discovery: Yup.string().required(t("error.required")),
                    customDiscoverySource: Yup.string().when("discovery", {
                        is: "different_source",
                        then: Yup.string().required(t("error.required")),
                        otherwise: Yup.string().notRequired()
                    }),
                    city: THEME.THEME_NAME != "dal" ? Yup.string().required(t("error.required")) : Yup.string().notRequired(),
                })}
            >
                {({ errors, touched, setFieldValue, isValid, dirty, values, setTouched }) => {
                    React.useEffect(() => {
                        if (!isValid && (!applicationStore.isStepFinished["personalData"] || !dirty)) {
                            applicationStore.canNavigate = false;
                            applicationStore.validateForm = () =>
                                setTouched(
                                    {
                                        firstName: true,
                                        lastName: true,
                                        jobCategoryId: true,
                                        phone: true,
                                        email: true,
                                        previousProjects: true,
                                        discovery: true,
                                        customDiscoverySource: true,
                                        city: true
                                    },
                                    true
                                );
                        } else {
                            applicationStore.canNavigate = true;
                        }

                        if (values && isValid) {
                            applicationStore.currentApplication = values;
                        }
                    }, [dirty, isValid, setTouched, values]);

                    const showDifferentDiscoverySourceField = values.discovery === "different_source";

                    return (
                        <Form style={{ height: "100%", flexGrow: 1 }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    justifyContent: isMobile ? "flex-start" : "space-between"
                                }}
                            >
                                <div>
                                    <PersonalDataGrid
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                        lastFieldMd={showDifferentDiscoverySourceField ? 6 : undefined}
                                        lastFieldLg={showDifferentDiscoverySourceField ? 6 : undefined}
                                        showCustomDiscoverySourceField={showDifferentDiscoverySourceField}
                                        customDiscoverySourceMd={6}
                                        customDiscoverySourceLg={6}
                                    />
                                    <ErrorMessagesList errors={errors} touched={touched} />
                                </div>

                                <ButtonContainer
                                    style={{
                                        marginTop: 18,
                                        justifyContent: THEME.THEME_NAME === "dal" ? "flex-end" : "center"
                                    }}
                                >
                                    <HrBotButton label={t("screen.personalData.furtherToDetails")} type="submit" />
                                </ButtonContainer>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
});
